// Hand-maintained — admin AI/LLM management namespace
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import {
  getAdminSysAiConfig,
  getAdminSysAiConfigById,
  postAdminSysAiConfig,
  patchAdminSysAiConfigById,
  getAdminLlmAnalytics,
  getAdminLlmAnalyticsById,
  postAdminLlmAnalytics,
  getAdminLlmAnalyticsCosts,
  getAdminLlmAnalyticsSummary,
  getAdminLlmAnalyticsUsage,
  getAdminLlmAnalyticsWorkspace,
  getAdminLlmAnalyticsPlatform,
  getAdminSysSemanticCacheStats,
  postAdminSysSemanticCacheClear,
} from "../_internal/sdk.gen";
import type { LlmAnalytics } from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import type { RequestBuilder } from "../request-builder";

// ---------------------------------------------------------------------------
// Typed attribute interfaces
// ---------------------------------------------------------------------------

/** LLM provider configuration for an application. */
export interface ProviderConfig {
  id?: string;
  cloud_model?: string;
  embedding_model?: string;
  vector_dimensions?: number;
  model_overrides?: Record<string, string>;
  fallback_chains?: Record<string, unknown> | null;
  pricing_floor_percent?: number | null;
  workload_backends?: Record<string, unknown> | null;
}

/**
 * Attributes accepted by the LLM analytics `:record` create action.
 *
 * Mirrors the server `GptCore.LLM.LlmAnalytics` `:record` accept list.
 * Note: the model attribute is `model_id` (NOT `model`). Decimal fields
 * (`cost_usd`, `wholesale_cost_usd`, `audio_seconds`) accept either a
 * number or a string to preserve precision over the wire.
 */
export interface RecordLlmAnalyticsAttributes {
  model_id: string;
  tenant_id?: string;
  workspace_id?: string;
  user_id?: string;
  agent_id?: string;
  provider?: string;
  task?: string;
  workload?: string;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  audio_seconds?: number | string;
  image_pages?: number;
  image_tokens?: number;
  character_count?: number;
  latency_ms?: number;
  cost_usd?: number | string;
  wholesale_cost_usd?: number | string;
  confidence_score?: number;
  metadata?: Record<string, unknown>;
  usage_event_id?: string;
}

/** LLM analytics cost breakdown. */
export interface LlmCostBreakdown {
  total_usd?: number;
  total_tokens?: number;
  by_model?: Array<{
    name?: string;
    usd?: number;
    tokens?: number;
  }>;
}

/** LLM analytics summary. */
export interface LlmAnalyticsSummary {
  total_requests?: number;
  avg_latency_ms?: number;
}

/** LLM analytics usage time-series. */
export interface LlmAnalyticsUsage {
  data_points?: Array<{
    timestamp?: string;
    tokens?: number;
    requests?: number;
  }>;
}

/** LLM workspace-level metrics. */
export interface LlmWorkspaceMetrics {
  top_agents?: Array<{ name?: string }>;
}

/** LLM platform-level summary metrics. */
export interface LlmPlatformSummary {
  total_requests?: number;
  total_tokens?: number;
  total_usd?: number;
}

/**
 * Creates the `ai` namespace for the admin SDK, providing methods for
 * LLM provider configuration, analytics, and semantic cache management.
 *
 * @param rb - The `RequestBuilder` instance bound to the authenticated client.
 * @returns An object containing AI management sub-namespaces.
 *
 * @example
 * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
 *
 * // List provider configs
 * const configs = await admin.ai.providerConfig.list();
 *
 * // Get analytics summary
 * const summary = await admin.ai.analytics.summary();
 *
 * // Clear semantic cache
 * await admin.ai.cache.clear(true);
 */
export function createAiNamespace(rb: RequestBuilder) {
  return {
    /**
     * Sub-namespace for managing LLM provider configurations.
     *
     * Provider configs control which LLM provider and model an application
     * uses for inference. Each application can have its own config.
     */
    providerConfig: {
      /**
       * Lists all provider configurations.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to an array of provider config objects.
       *
       * @example
       * const configs = await admin.ai.providerConfig.list();
       * console.log(`Provider configs: ${configs.length}`);
       */
      list: async (options?: RequestOptions): Promise<ProviderConfig[]> => {
        return rb.execute<ProviderConfig[]>(getAdminSysAiConfig, {}, options);
      },

      /**
       * Gets a provider configuration by ID.
       *
       * @param id - The UUID of the provider config.
       * @param options - Optional request options.
       * @returns A promise that resolves to the provider config object.
       *
       * @example
       * const config = await admin.ai.providerConfig.get('cfg_01HXYZ...');
       * console.log(`Model: ${config.cloud_model}`);
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ProviderConfig> => {
        return rb.execute<ProviderConfig>(
          getAdminSysAiConfigById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates or upserts a provider configuration.
       *
       * @param attributes - The provider config attributes.
       * @param options - Optional request options.
       * @returns A promise that resolves to the created provider config.
       *
       * @example
       * const config = await admin.ai.providerConfig.create({
       *   cloud_model: 'google/gemini-2.5-pro',
       *   embedding_model: 'text-embedding-004',
       * });
       */
      create: async (
        attributes: Partial<ProviderConfig>,
        options?: RequestOptions,
      ): Promise<ProviderConfig> => {
        const {
          id,
          cloud_model,
          embedding_model,
          vector_dimensions,
          model_overrides,
          fallback_chains,
          pricing_floor_percent,
          workload_backends,
        } = attributes;
        return rb.execute<ProviderConfig>(
          postAdminSysAiConfig,
          {
            body: {
              data: {
                type: "ai-config",
                attributes: {
                  id,
                  cloud_model,
                  embedding_model,
                  vector_dimensions,
                  model_overrides,
                  fallback_chains,
                  pricing_floor_percent,
                  workload_backends,
                },
              },
            },
          },
          options,
        );
      },

      /**
       * Updates a provider configuration by ID.
       *
       * @param id - The UUID of the provider config to update.
       * @param attributes - The attributes to update.
       * @param options - Optional request options.
       * @returns A promise that resolves to the updated provider config.
       *
       * @example
       * const updated = await admin.ai.providerConfig.update('cfg_01HXYZ...', {
       *   cloud_model: 'gpt-4o-mini',
       * });
       */
      update: async (
        id: string,
        attributes: Partial<ProviderConfig>,
        options?: RequestOptions,
      ): Promise<ProviderConfig> => {
        const {
          cloud_model,
          embedding_model,
          vector_dimensions,
          model_overrides,
          fallback_chains,
          pricing_floor_percent,
          workload_backends,
        } = attributes;
        return rb.execute<ProviderConfig>(
          patchAdminSysAiConfigById,
          {
            path: { id },
            body: {
              data: {
                type: "ai-config",
                id,
                attributes: {
                  cloud_model,
                  embedding_model,
                  vector_dimensions,
                  model_overrides,
                  fallback_chains,
                  pricing_floor_percent,
                  workload_backends,
                },
              },
            },
          },
          options,
        );
      },
    },

    /**
     * Sub-namespace for querying LLM analytics and usage metrics.
     *
     * Provides admin-level access to token usage, costs, and platform-wide
     * metrics across all tenants and workspaces.
     */
    analytics: {
      /**
       * Lists raw LLM analytics records.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to an array of analytics records.
       *
       * @example
       * const records = await admin.ai.analytics.list();
       * console.log(`Total records: ${records.length}`);
       */
      list: async (options?: RequestOptions): Promise<LlmAnalytics[]> => {
        return rb.execute<LlmAnalytics[]>(getAdminLlmAnalytics, {}, options);
      },

      /**
       * Gets a single LLM analytics record by ID.
       *
       * @param id - The UUID of the analytics record.
       * @param options - Optional request options.
       * @returns A promise that resolves to the analytics record.
       *
       * @example
       * const record = await admin.ai.analytics.get("lla_01HXYZ...");
       * console.log(record.attributes?.model_id);
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<LlmAnalytics> => {
        return rb.execute<LlmAnalytics>(
          getAdminLlmAnalyticsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Records a new LLM analytics entry.
       *
       * @param attributes - The analytics entry attributes.
       * @param options - Optional request options.
       * @returns A promise that resolves to the created record.
       *
       * @example
       * const entry = await admin.ai.analytics.record({
       *   model_id: "gpt-4o-mini",
       *   provider: "openrouter",
       *   task: "completion",
       *   workload: "chat",
       *   input_tokens: 1200,
       *   output_tokens: 350,
       *   total_tokens: 1550,
       *   latency_ms: 820,
       *   cost_usd: "0.0042",
       * });
       * console.log(entry.id);
       */
      record: async (
        attributes: RecordLlmAnalyticsAttributes,
        options?: RequestOptions,
      ): Promise<LlmAnalytics> => {
        const {
          tenant_id,
          workspace_id,
          user_id,
          agent_id,
          model_id,
          provider,
          task,
          workload,
          input_tokens,
          output_tokens,
          total_tokens,
          audio_seconds,
          image_pages,
          image_tokens,
          character_count,
          latency_ms,
          cost_usd,
          wholesale_cost_usd,
          confidence_score,
          metadata,
          usage_event_id,
        } = attributes;
        return rb.execute<LlmAnalytics>(
          postAdminLlmAnalytics,
          {
            body: {
              data: {
                type: "llm-analytics",
                attributes: {
                  tenant_id,
                  workspace_id,
                  user_id,
                  agent_id,
                  model_id,
                  provider,
                  task,
                  workload,
                  input_tokens,
                  output_tokens,
                  total_tokens,
                  audio_seconds,
                  image_pages,
                  image_tokens,
                  character_count,
                  latency_ms,
                  cost_usd,
                  wholesale_cost_usd,
                  confidence_score,
                  metadata,
                  usage_event_id,
                },
              },
            },
          },
          options,
        );
      },

      /**
       * Returns a token cost breakdown grouped by model and time period.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to a cost breakdown object.
       *
       * @example
       * const costs = await admin.ai.analytics.costs();
       * console.log(`Total spend: $${costs.total_usd}`);
       */
      costs: async (options?: RequestOptions): Promise<LlmCostBreakdown> => {
        return rb.execute<LlmCostBreakdown>(
          getAdminLlmAnalyticsCosts,
          {},
          options,
        );
      },

      /**
       * Returns a high-level summary of LLM usage, optionally filtered by date range.
       *
       * @param params - Optional filter parameters.
       * @param params.startDate - ISO 8601 start date for the summary period.
       * @param params.endDate - ISO 8601 end date for the summary period.
       * @param options - Optional request options.
       * @returns A promise that resolves to a summary metrics object.
       *
       * @example
       * const summary = await admin.ai.analytics.summary();
       * const filtered = await admin.ai.analytics.summary({
       *   startDate: "2026-01-01",
       *   endDate: "2026-03-31",
       * });
       */
      summary: async (
        params?: { startDate?: string; endDate?: string },
        options?: RequestOptions,
      ): Promise<LlmAnalyticsSummary> => {
        const query: Record<string, string> = {};
        if (params?.startDate) query.start_date = params.startDate;
        if (params?.endDate) query.end_date = params.endDate;
        return rb.execute<LlmAnalyticsSummary>(
          getAdminLlmAnalyticsSummary,
          { query },
          options,
        );
      },

      /**
       * Returns time-series LLM usage data points.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to usage time-series data.
       *
       * @example
       * const usage = await admin.ai.analytics.usage();
       * for (const point of usage.data_points) {
       *   console.log(`${point.timestamp}: ${point.tokens} tokens`);
       * }
       */
      usage: async (options?: RequestOptions): Promise<LlmAnalyticsUsage> => {
        return rb.execute<LlmAnalyticsUsage>(
          getAdminLlmAnalyticsUsage,
          {},
          options,
        );
      },

      /**
       * Returns workspace-scoped LLM metrics.
       *
       * @param workspaceId - The UUID of the workspace to query metrics for.
       * @param options - Optional request options.
       * @returns A promise that resolves to workspace metrics.
       *
       * @example
       * const metrics = await admin.ai.analytics.workspace("ws_abc123");
       */
      workspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<LlmWorkspaceMetrics> => {
        return rb.execute<LlmWorkspaceMetrics>(
          getAdminLlmAnalyticsWorkspace,
          { query: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Returns platform-wide LLM summary across all applications and tenants.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to platform summary metrics.
       *
       * @example
       * const platform = await admin.ai.analytics.platformSummary();
       * console.log(`Platform total: $${platform.total_usd}`);
       */
      platformSummary: async (
        options?: RequestOptions,
      ): Promise<LlmPlatformSummary> => {
        return rb.execute<LlmPlatformSummary>(
          getAdminLlmAnalyticsPlatform,
          {},
          options,
        );
      },
    },

    /**
     * Sub-namespace for semantic cache management.
     *
     * The semantic cache stores LLM prompt/response pairs indexed by vector
     * similarity. Admins can inspect cache entries and clear the cache.
     */
    cache: {
      /**
       * Gets semantic cache statistics.
       *
       * @param options - Optional request options.
       * @returns A promise that resolves to the cache stats (entry counts,
       *   hit/miss totals, and storage metrics).
       *
       * @example
       * const stats = await admin.ai.cache.stats();
       * console.log(stats);
       */
      stats: async (options?: RequestOptions): Promise<unknown> => {
        return rb.execute<unknown>(getAdminSysSemanticCacheStats, {}, options);
      },

      /**
       * Clears the semantic cache.
       *
       * Removes all cached prompt/response pairs. This is irreversible.
       * Requires `confirm: true` as a safety gate.
       *
       * @param confirm - Must be `true` to confirm the destructive operation.
       * @param options - Optional request options.
       * @returns A promise that resolves when the cache is cleared.
       *
       * @example
       * await admin.ai.cache.clear(true);
       * console.log('Semantic cache cleared');
       */
      clear: async (
        confirm: boolean,
        options?: RequestOptions,
      ): Promise<unknown> => {
        return rb.execute<unknown>(
          postAdminSysSemanticCacheClear,
          {
            body: {
              data: {
                type: "semantic-cache-entry",
                attributes: { confirm },
              },
            },
          },
          options,
        );
      },
    },
  };
}
